import React, { useState, useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import Controls from './Controls';

import roundPause from '../../assets/roundPause.svg';
import roundPlay from '../../assets/roundPlay.svg';
import { createImageUrl } from '../../services/data';
import { safePlay } from '../../utils/errorHandler';
import { trackAudioPlayed, trackAudioFinished } from '../../mixpanel';

import styles from './Player.module.css'
import ProgressBar from './ProgressBar';

interface AudioPlayerProps {
  audio: string;
  name?: string;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({audio, name}) => {
    const [isPlaying, setIsPlaying] = useState<boolean>(false)
    const [duration, setDuration] = useState<number>(0)
    const [currentTime, setCurrentTime] = useState<number>(0);
    const audioRef = useRef<HTMLAudioElement>(null)
    const hasTrackedPlay = useRef(false)

    useEffect(() => {
        if (!isPlaying) {
            audioRef.current?.pause();
        } else {
            safePlay(audioRef.current);
            if (!hasTrackedPlay.current) {
                hasTrackedPlay.current = true;
                trackAudioPlayed(name ?? audio);
            }
        }
    }, [isPlaying]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleLoadeMetaData = () => {
        setDuration(audioRef.current!.duration)
    }

    const handleTimeUpdate = () => {
        setCurrentTime(audioRef.current!.currentTime)
    }

    const handleSeek = (time: number) => {
        audioRef.current!.currentTime = time
    }

    const handleEnded = () => {
        setIsPlaying(false);
        hasTrackedPlay.current = false;
        trackAudioFinished(name ?? audio);
    }

    return (
        <Box>
            <Box mt='27%'>
                <audio
                    ref={audioRef}
                    src={createImageUrl(audio)}
                    onLoadedMetadata={handleLoadeMetaData}
                    onTimeUpdate={handleTimeUpdate}
                    onEnded={handleEnded}
                />
            </Box>
            <ProgressBar
                playerRef={audioRef}
                progress={currentTime}
                totalDuration={duration}
                onSeek={handleSeek}
            />
            <Controls
                totalDuration={duration}
                playerRef={audioRef}
                setIsPlaying={setIsPlaying}
                progress={currentTime}
            />
            <div className={styles.access}>
                <button onClick={() => setIsPlaying(!isPlaying)} aria-label='toggle play/pause'>
                    <img src={isPlaying? roundPause : roundPlay } alt={isPlaying? 'pause' : 'play'} />
                </button>
            </div>
        </Box>
    );
};

export default AudioPlayer;
